import React, { useCallback, useEffect, useState } from "react";
import { Dropdown, Pagination, Container, Row, Col } from "react-bootstrap";

/**
 * a component that overrides the default pagination component.
 * It must satisfy the following API:
 *  {
 *       rowsPerPage: PropTypes.number.isRequired,
 *       rowCount: PropTypes.number.isRequired,
 *       currentPage: PropTypes.number.isRequired,
 *       onChangePage: PropTypes.func.isRequired,
 *       onChangeRowsPerPage: PropTypes.func.isRequired,
 *  };
 */
const RowsPerPageOptions = [10, 25, 50, 100];

const TablePagination = ({
  currentPage,
  rowCount,
  rowsPerPage,
  onChangeRowsPerPage,
  onChangePage: onChangePageApi,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [pageValue, setPageValue] = useState({
    lastTimeout: null,
    value: currentPage,
  });

  const onChangePage = useCallback(
    (page) => {
      if (page > totalPages) {
        onChangePageApi(totalPages, rowCount);
        setPageValue({ value: totalPages });
      } else if (page >= 1) {
        onChangePageApi(page, rowCount);
        setPageValue({ value: page });
      } else {
        onChangePageApi(1, rowCount);
        setPageValue({ value: "1" });
      }
    },
    [totalPages, rowCount, setPageValue, onChangePageApi],
  );

  useEffect(() => {
    setTotalPages(Math.ceil(rowCount / rowsPerPage));
    setPageValue({ lastTimeout: null, value: currentPage });
  }, [rowCount, rowsPerPage, currentPage]);

  return (
    <Container className="d-flex w-100 justify-content-end w-100 p-0 mt-4 mr-0">
      <Row>
        <Col sm="4">
          <Dropdown>
            <Dropdown.Toggle
              as="a"
              className="p-1 small dropdown-type-link cursor-pointer"
            >{`${rowsPerPage} por página`}</Dropdown.Toggle>
            <Dropdown.Menu>
              {RowsPerPageOptions.map((item) => (
                <Dropdown.Item
                  key={item}
                  value={item}
                  active={item === rowsPerPage}
                  onSelect={() => {
                    onChangeRowsPerPage(item);
                  }}
                >
                  {item}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col sm="8">
          <Pagination size="sm">
            <Pagination.First disabled={currentPage === 1} onClick={() => onChangePage(1)} />
            <Pagination.Prev disabled={currentPage - 1 === 0} onClick={() => onChangePage(currentPage - 1)} />
            {totalPages ? (
              <span className="d-flex flex-inline">
                <input
                  className="form-control form-control-sm text-center rounded-0 border-left-0"
                  type="number"
                  max={totalPages}
                  min={1}
                  value={pageValue.value}
                  onChange={(e) => {
                    if (pageValue.lastTimeout) {
                      clearTimeout(pageValue.lastTimeout);
                    }

                    setPageValue({
                      lastTimeout: setTimeout(() => {
                        onChangePage(e.target.value);
                      }, 750),
                      value: e.target.value,
                    });
                  }}
                />
                &nbsp;
                <span className="p-2 small w-100">{`de ${totalPages}`}</span>
              </span>
            ) : (
              "-"
            )}
            <Pagination.Next disabled={currentPage === totalPages} onClick={() => onChangePage(currentPage + 1)} />
            <Pagination.Last disabled={currentPage === totalPages} onClick={() => onChangePage(totalPages)} />
          </Pagination>
        </Col>
      </Row>
    </Container>
  );
};

export default TablePagination;
